/**
 * OTP Utility Functions for Super Wear
 * Handles mobile number validation, OTP generation, and verification
 */

import { authService } from '../services/api';

// Pakistani mobile number patterns (updated for backend compatibility)
const MOBILE_PATTERNS = [
  /^0\d{10}$/, // 03001234567 (local format)
  /^\+92\d{10}$/ // +923001234567 (international format)
];

/**
 * Validates Pakistani mobile number format
 * @param {string} mobile - Mobile number to validate
 * @returns {boolean} - True if valid Pakistani mobile number
 */
export const validateMobileNumber = (mobile) => {
  if (!mobile || typeof mobile !== 'string') {
    return false;
  }
  
  const cleanMobile = mobile.trim();
  return MOBILE_PATTERNS.some(pattern => pattern.test(cleanMobile));
};

/**
 * Formats mobile number for display
 * @param {string} value - Raw mobile number input
 * @returns {string} - Formatted mobile number
 */
export const formatMobileNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digits and spaces
  const cleanValue = value.replace(/[^\d+]/g, '');
  
  // Handle international format (+92)
  if (cleanValue.startsWith('+92')) {
    const digits = cleanValue.slice(3); // Remove +92
    if (digits.length <= 10) {
      return `+92${digits}`;
    }
  }
  
  // Handle local format (0)
  if (cleanValue.startsWith('0')) {
    const digits = cleanValue.slice(1); // Remove 0
    if (digits.length <= 10) {
      return `0${digits}`;
    }
  }
  
  // Handle raw digits
  if (/^\d+$/.test(cleanValue)) {
    if (cleanValue.length <= 10) {
      return cleanValue;
    } else if (cleanValue.length === 11 && cleanValue.startsWith('0')) {
      return cleanValue;
    } else if (cleanValue.length === 12 && cleanValue.startsWith('92')) {
      return `+${cleanValue}`;
    }
  }
  
  return cleanValue;
};

/**
 * Generates a 6-digit OTP
 * @returns {string} - 6-digit OTP
 */
export const generateOTP = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Validates OTP format
 * @param {string} otp - OTP to validate
 * @returns {boolean} - True if valid 6-digit OTP
 */
export const validateOTP = (otp) => {
  return /^[0-9]{6}$/.test(otp);
};

/**
 * Stores OTP temporarily in sessionStorage
 * @param {string} otp - OTP to store
 * @param {string} mobile - Mobile number associated with OTP
 */
export const storeOTP = (otp, mobile) => {
  try {
    sessionStorage.setItem('temp_otp', otp);
    sessionStorage.setItem('temp_mobile', mobile);
    sessionStorage.setItem('otp_timestamp', Date.now().toString());
  } catch (error) {
    console.error('Failed to store OTP:', error);
  }
};

/**
 * Retrieves stored OTP from sessionStorage
 * @returns {object} - Object containing otp, mobile, and timestamp
 */
export const getStoredOTP = () => {
  try {
    const otp = sessionStorage.getItem('temp_otp');
    const mobile = sessionStorage.getItem('temp_mobile');
    const timestamp = sessionStorage.getItem('otp_timestamp');
    
    return {
      otp,
      mobile,
      timestamp: timestamp ? parseInt(timestamp) : null
    };
  } catch (error) {
    console.error('Failed to retrieve OTP:', error);
    return { otp: null, mobile: null, timestamp: null };
  }
};

/**
 * Clears stored OTP from sessionStorage
 */
export const clearStoredOTP = () => {
  try {
    sessionStorage.removeItem('temp_otp');
    sessionStorage.removeItem('temp_mobile');
    sessionStorage.removeItem('otp_timestamp');
  } catch (error) {
    console.error('Failed to clear OTP:', error);
  }
};

/**
 * Checks if OTP has expired (default: 10 minutes)
 * @param {number} timestamp - OTP creation timestamp
 * @param {number} expiryMinutes - Expiry time in minutes (default: 10)
 * @returns {boolean} - True if OTP has expired
 */
export const isOTPExpired = (timestamp, expiryMinutes = 10) => {
  if (!timestamp) return true;
  
  const now = Date.now();
  const expiryTime = timestamp + (expiryMinutes * 60 * 1000);
  
  return now > expiryTime;
};

/** Default (`whatsapp` or any value except `local`) = backend Bird WhatsApp OTP. `local` = client-only demo. */
function signupOtpUsesBackendApi() {
  const mode = (process.env.REACT_APP_SIGNUP_OTP_MODE || 'whatsapp').toLowerCase().trim();
  return mode !== 'local';
}

/**
 * Verifies OTP against sessionStorage (local / demo mode only — not for production).
 * @param {string} inputOTP
 * @param {string} mobile
 */
function verifyOTPLocal(inputOTP, mobile) {
  const stored = getStoredOTP();

  if (!stored.otp || !stored.mobile || !stored.timestamp) {
    return {
      success: false,
      message: 'No OTP found. Please request a new one.',
    };
  }

  if (isOTPExpired(stored.timestamp)) {
    clearStoredOTP();
    return {
      success: false,
      message: 'OTP has expired. Please request a new one.',
    };
  }

  if (stored.mobile !== mobile) {
    return {
      success: false,
      message: 'Mobile number mismatch. Please use the same number.',
    };
  }

  if (stored.otp !== inputOTP) {
    return {
      success: false,
      message: 'Invalid OTP. Please try again.',
    };
  }

  clearStoredOTP();

  return {
    success: true,
    message: 'OTP verified successfully!',
  };
}

/** @param {string} mobile */
async function sendOTPLocal(mobile) {
  const otp = generateOTP();
  storeOTP(otp, mobile);
  await new Promise((resolve) => setTimeout(resolve, 600));
  if (process.env.NODE_ENV === 'development') {
    console.warn('[phone OTP local mode] demo code (remove in prod):', mobile, otp);
  }
  return {
    success: true,
    message: `Demo code generated for ${mobile}. (Set REACT_APP_SIGNUP_OTP_MODE to default and use backend Bird WhatsApp OTP.)`,
    channel: 'local',
  };
}

/**
 * Verifies the 6-digit code. Default: backend Bird WhatsApp OTP. Paths remain `/auth/signup/whatsapp-otp/*`.
 * @returns {Promise<{ success: boolean, message: string, phoneVerificationToken?: string | null }>}
 */
export const verifyOTP = async (inputOTP, mobile) => {
  if (!validateOTP(inputOTP)) {
    return { success: false, message: 'Please enter a valid 6-digit code.', phoneVerificationToken: null };
  }

  if (!signupOtpUsesBackendApi()) {
    const r = verifyOTPLocal(inputOTP.trim(), mobile.trim());
    return { ...r, phoneVerificationToken: null };
  }

  try {
    const res = await authService.verifySignupWhatsappOtp({
      phone: mobile.trim(),
      code: inputOTP.trim(),
    });
    if (res.success) {
      clearStoredOTP();
      return {
        success: true,
        message: res.message || 'Mobile number verified!',
        phoneVerificationToken: res.phoneVerificationToken || null,
      };
    }
    return {
      success: false,
      message: res.message || 'Invalid or expired code.',
      phoneVerificationToken: null,
    };
  } catch (error) {
    let msg = 'Verification failed. Please try again.';
    try {
      const p = JSON.parse(error.message);
      if (p?.error) msg = String(p.error);
    } catch {
      /* ignore */
    }
    return { success: false, message: msg, phoneVerificationToken: null };
  }
};

/**
 * Sends verification code via backend (Bird WhatsApp). `REACT_APP_SIGNUP_OTP_MODE=local` for demo only.
 * @param {string} mobile
 */
export const sendOTP = async (mobile) => {
  try {
    if (!validateMobileNumber(mobile)) {
      throw new Error('Invalid mobile number format');
    }

    if (!signupOtpUsesBackendApi()) {
      return sendOTPLocal(mobile);
    }

    const res = await authService.sendSignupWhatsappOtp(mobile.trim());
    if (res.success) {
      return {
        success: true,
        message: res.message || 'Verification code sent to your WhatsApp.',
        channel: 'whatsapp',
      };
    }
    return {
      success: false,
      message: res.message || 'Failed to send verification code.',
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to send OTP',
    };
  }
};
/**
 * Forgot password: verify WhatsApp code (Bird). Uses `/auth/forgotpassword/whatsapp-otp/verify`.
 * @returns {Promise<{ success: boolean, message: string, phoneVerificationToken?: string | null }>}
 */
export const verifyForgotPasswordOTP = async (inputOTP, mobile) => {
  if (!validateOTP(inputOTP)) {
    return { success: false, message: 'Please enter a valid 6-digit code.', phoneVerificationToken: null };
  }

  if (!signupOtpUsesBackendApi()) {
    const r = verifyOTPLocal(inputOTP.trim(), mobile.trim());
    return { ...r, phoneVerificationToken: null };
  }

  try {
    const res = await authService.verifyForgotPasswordWhatsappOtp({
      phone: mobile.trim(),
      code: inputOTP.trim(),
    });
    if (res.success) {
      clearStoredOTP();
      return {
        success: true,
        message: res.message || 'Mobile number verified!',
        phoneVerificationToken: res.phoneVerificationToken || null,
      };
    }
    return {
      success: false,
      message: res.message || 'Invalid or expired code.',
      phoneVerificationToken: null,
    };
  } catch (error) {
    let msg = 'Verification failed. Please try again.';
    try {
      const p = JSON.parse(error.message);
      if (p?.error) msg = String(p.error);
    } catch {
      /* ignore */
    }
    return { success: false, message: msg, phoneVerificationToken: null };
  }
};

/**
 * Forgot password: send WhatsApp code (Bird). Uses `/auth/forgotpassword/whatsapp-otp/send` for **registered** phones.
 * @param {string} mobile
 */
export const sendForgotPasswordOTP = async (mobile) => {
  try {
    if (!validateMobileNumber(mobile)) {
      throw new Error('Invalid mobile number format');
    }

    if (!signupOtpUsesBackendApi()) {
      return sendOTPLocal(mobile);
    }

    const res = await authService.sendForgotPasswordWhatsappOtp(mobile.trim());
    if (res.success) {
      return {
        success: true,
        message: res.message || 'Verification code sent to your WhatsApp.',
        channel: 'whatsapp',
      };
    }
    return {
      success: false,
      message: res.message || 'Failed to send verification code.',
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Failed to send OTP',
    };
  }
};

/**
 * Formats remaining time for OTP resend countdown
 * @param {number} seconds - Remaining seconds
 * @returns {string} - Formatted time string
 */
export const formatCountdown = (seconds) => {
  if (seconds <= 0) return 'Resend code';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `Resend (${minutes}:${remainingSeconds.toString().padStart(2, '0')})`;
  }
  
  return `Resend (${remainingSeconds}s)`;
};

/**
 * Validates password strength according to backend requirements
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with success status and message
 */
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return {
      success: false,
      message: 'Password is required'
    };
  }
  
  if (password.length < 6) {
    return {
      success: false,
      message: 'Password must be at least 6 characters long'
    };
  }
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  
  if (!hasUpperCase) {
    return {
      success: false,
      message: 'Password must contain at least one uppercase letter'
    };
  }
  
  if (!hasLowerCase) {
    return {
      success: false,
      message: 'Password must contain at least one lowercase letter'
    };
  }
  
  if (!hasNumber) {
    return {
      success: false,
      message: 'Password must contain at least one number'
    };
  }
  
  return {
    success: true,
    message: 'Password is strong'
  };
};

/**
 * Validates name according to backend requirements
 * @param {string} name - Name to validate
 * @returns {object} - Validation result with success status and message
 */
export const validateName = (name) => {
  if (!name || typeof name !== 'string') {
    return {
      success: false,
      message: 'Name is required'
    };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 2) {
    return {
      success: false,
      message: 'Name must be at least 2 characters long'
    };
  }
  
  if (trimmedName.length > 50) {
    return {
      success: false,
      message: 'Name must be less than 50 characters'
    };
  }
  
  // Check if name contains only letters, spaces, and common punctuation
  if (!/^[a-zA-Z\s\-'\.]+$/.test(trimmedName)) {
    return {
      success: false,
      message: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods'
    };
  }
  
  return {
    success: true,
    message: 'Name is valid'
  };
}; 